import { Attribute } from './Attribute';
import { MemoKey, MemoStore } from './MemoStore';

const isEqual = ((a: any[], b: any[]) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
});

type Selector = (target: any) => any[];
type Accessor = (target: any) => any;

const createAccessor = (prop: MemoUsingInput) => {
    if (prop instanceof Function) return prop;
    if (prop instanceof Attribute) return (target: any) => prop.get(target);
    const path = prop.split('.');
    return (target: any) => path.reduce((acc, x) => {
        // If bang, remove and toString the result
        if (typeof (x) === 'string' && x.endsWith('!')) {
            return acc?.[x.slice(0, -1)]?.toString();
        }
        return acc?.[x];
    }, target);
};

const createSelector = (accessors: Accessor[]) =>
    (target: any) => accessors.map(a => a(target));

const createMemoized = (name: string, originalGetter: any, selector: Selector) => {
    const key = MemoKey.create(name);
    return function () {

        const store = MemoStore.get(this);
        const source = selector(this);
        const lastResult = store.getIfHit(key, source);
        if (lastResult) return lastResult;

        const result = originalGetter.call(this);
        store.set(key, source, result);
        return result;
    };
};

export type MemoUsingInput = string | ((target: any) => any) | Attribute<any>;

export function memoUsing(...props: MemoUsingInput[]) {
    return (_target: any, name: string | symbol, descriptor: PropertyDescriptor) => {
        // Prevent use on symbol properties
        if (typeof (name) !== 'string') throw new Error('cache failure: Cannot use symbol as cache key.');
        const accessors = props.map(x => createAccessor(x));
        const selector = createSelector(accessors);
        descriptor.get = createMemoized(name, descriptor.get, selector);
        return descriptor;
    };
}
